import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import ExtraMileHeader from '../../components/ExtraMileHeader/ExtraMileHeader';
import { loginUser, logout } from '../../store/authSlice';
import { setBackButtonUrl } from '../../store/uiSlice';
import { INTRO_GIF } from '../../data/brainTeasers';
import './login.css';

function safeRedirectUrl(url) {
  const fallback =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '/') ||
    '/';
  if (!url || typeof url !== 'string') return fallback;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return fallback;
  }
}

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { status, user, error } = useSelector((s) => s.auth);

  useEffect(() => {
    const otp = searchParams.get('otp');
    const demo = searchParams.get('demo');
    if (otp || demo) {
      dispatch(logout());
    }
    dispatch(loginUser());
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (status === 'succeeded' && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  /* PHP index.php: after login, show intro GIF then auto-advance to rules */
  useEffect(() => {
    if (status !== 'succeeded' || !user) return undefined;
    document.body.classList.add('bt-round-active');
    const t = setTimeout(() => navigate('/rules'), 7000);
    return () => {
      clearTimeout(t);
      document.body.classList.remove('bt-round-active');
    };
  }, [status, user, navigate]);

  useEffect(() => {
    if (status !== 'failed') return;
    const msg = error ? String(error) : 'Session expired';
    const redirect = safeRedirectUrl(user?.backButtonRedirect);
    const title =
      msg.includes('OTP is required') || msg.includes('No saved user')
        ? 'Login required'
        : 'Session Expired!';
    Swal.fire({
      title,
      text: msg,
      icon: 'error',
      confirmButtonText: 'Close',
      customClass: {
        confirmButton: 'hf-swal-close',
        popup: 'hf-swal-popup',
      },
      buttonsStyling: false,
    }).then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  const shell = (body, { intro = false } = {}) => (
    <div className={`login-shell${intro ? ' login-shell-intro' : ''}`}>
      <ExtraMileHeader />
      <div className={`login-page${intro ? ' login-page-intro' : ''}`}>{body}</div>
    </div>
  );

  if (status === 'loading' || status === 'idle') {
    return shell(<div className="login-loader" />);
  }

  if (status === 'succeeded' && user) {
    return shell(
      <>
        <div className="login-intro-fullbleed" aria-hidden />
        <img src={INTRO_GIF} alt="Brain Teasers" className="login-intro-gif" />
      </>,
      { intro: true }
    );
  }

  return shell(<div className="login-loader" />);
}
